import { useEffect, useState } from "react";
import { PasswordInput } from "@/components/shop/PasswordInput";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, Trash2 } from "lucide-react";
import { api, eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inp = "h-11 w-full rounded-xl border border-black/15 bg-white px-3 text-sm outline-none focus:border-amber-500";

export const download = async (url, filename) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = filename;
    a.click();
  } catch (err) {
    toast.error(formatApiError(err));
  }
};

const Billing = () => {
  const { user, checkAuth } = useAuth();
  const b = user.b2b || {};
  const [f, setF] = useState({ address: b.address || "", zip: b.zip || "", city: b.city || "", province: b.province || "", sdi: b.sdi || "", pec: b.pec || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/account/b2b-billing", { ...f, province: f.province || null, sdi: f.sdi || null, pec: f.pec || null });
      toast.success("Dati di fatturazione salvati"); checkAuth();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className="grid gap-3 rounded-2xl border border-black/10 bg-white p-5 sm:grid-cols-2" data-testid="b2b-billing-form">
      <p className="font-display text-lg font-bold sm:col-span-2">Dati di fatturazione · <span data-no-translate>{b.company} · P.IVA {b.vat}</span></p>
      {[["address", "Indirizzo sede", "sm:col-span-2", true], ["zip", "CAP", "", true], ["city", "Città", "", true], ["province", "Provincia (sigla)", "", false], ["sdi", "Codice SDI (7 caratteri)", "", false], ["pec", "PEC (se non hai il codice SDI)", "sm:col-span-2", false]].map(([k, l, span, req]) => (
        <label key={k} className={`space-y-1 ${span}`}><span className="text-xs text-stone-600">{l}</span>
          <input required={req} value={f[k]} onChange={set(k)} maxLength={k === "province" ? 2 : k === "sdi" ? 7 : 120} className={inp} data-testid={`billing-${k}`} /></label>
      ))}
      <button className="h-11 rounded-full bg-amber-500 font-bold text-black sm:col-span-2" data-testid="billing-save">Salva</button>
    </form>
  );
};

const DeleteAccount = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const del = async () => {
    try {
      await api.post("/account/delete", { confirm, password });
      toast.success("Account e dati personali cancellati");
      setUser?.(null);
      navigate("/");
      window.location.reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="rounded-2xl border border-rose-300 bg-rose-50/40 p-5" data-testid="delete-account">
      <p className="font-display text-lg font-bold">Cancella il mio account</p>
      <p className="mt-1 text-sm text-stone-700">Cancelliamo subito account, preferiti, recensioni (rese anonime), liste d'attesa e newsletter. Gli ordini restano anonimi per i 10 anni previsti dalla legge fiscale. L'operazione non si può annullare e il credito negozio residuo andrà perso.</p>
      {!open ? (
        <button onClick={() => setOpen(true)} className="mt-3 flex items-center gap-2 rounded-full border border-rose-500 px-4 py-2 text-sm font-semibold text-rose-600" data-testid="delete-account-open"><Trash2 className="h-4 w-4" />Cancella account</button>
      ) : (
        <div className="mt-3 space-y-2">
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Scrivi ELIMINA" className={inp} data-testid="delete-confirm-input" />
          {user.auth_provider === "email" && <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder="La tua password" className={inp} data-testid="delete-password-input" />}
          <button disabled={confirm.trim().toUpperCase() !== "ELIMINA"} onClick={del} className="rounded-full bg-rose-600 px-5 py-2 text-sm font-bold text-white disabled:opacity-40" data-testid="delete-account-confirm">Cancella definitivamente</button>
        </div>
      )}
    </div>
  );
};

export const MyData = () => {
  const { user } = useAuth();
  return (
    <div className="space-y-5" data-testid="my-data">
      {user.account_type === "b2b" && <Billing />}
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <p className="font-display text-lg font-bold">Scarica i miei dati</p>
        <p className="mt-1 text-sm text-stone-700">Ricevi un file con profilo, ordini, recensioni, liste d'attesa, iscrizioni e fatture (diritto di accesso e portabilità, artt. 15 e 20 GDPR).</p>
        <button onClick={() => download("/account/export", "i-miei-dati-cardsource.json")} className="mt-3 flex items-center gap-2 rounded-full border-2 border-black px-4 py-2 text-sm font-semibold" data-testid="export-data"><Download className="h-4 w-4" />Scarica i miei dati</button>
      </div>
      {user.role !== "admin" && <DeleteAccount />}
    </div>
  );
};

const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const refundMsg = (r) => (r.status === "eseguito" ? `Rimborso di ${eur(r.refunded ?? r.amount)} eseguito` : r.status === "errore" ? `Rimborso non riuscito: ${r.note}` : r.note || "Rimborso da fare a mano");

const RefundBadge = ({ nc, onDone }) => {
  const r = nc.refund;
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("Bonifico");
  const [date, setDate] = useState(localDay());
  const act = async (path, body) => {
    try { const { data } = await api.post(`/admin/invoices/${nc.id}/${path}`, body); toast.success(refundMsg(data)); setOpen(false); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!r) return <button onClick={() => window.confirm(`Rimborsare ${eur(nc.total)} al cliente?`) && act("refund")} className="text-xs text-rose-600 underline" data-testid={`refund-start-${nc.id}`}>Rimborsa cliente</button>;
  const color = r.status === "eseguito" ? "bg-teal-500/15 text-teal-700" : r.status === "errore" ? "bg-rose-500/15 text-rose-700" : "bg-amber-500/15 text-amber-700";
  const label = r.status === "eseguito" ? `Rimborsato ${[r.stripe?.length ? `su carta/PayPal ${eur(r.stripe.reduce((s, x) => s + x.amount, 0))}` : "", r.manual ? `${r.manual.method} ${eur(r.manual.amount ?? r.amount)}, ${r.manual.date}` : ""].filter(Boolean).join(" + ")}` : r.status === "errore" ? "Rimborso non riuscito" : r.status === "in_corso" ? "Rimborso in corso" : "Rimborso da fare a mano";
  return (
    <>
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${color}`} title={r.note || ""} data-testid={`refund-status-${nc.id}`}>{label}</span>
      {r.status === "errore" && r.mode === "stripe" && <button onClick={() => act("refund")} className="text-xs text-cyan-700 underline" data-testid={`refund-retry-${nc.id}`}>Riprova rimborso</button>}
      {["da_fare", "errore"].includes(r.status) && <button onClick={() => setOpen(!open)} className="text-xs text-stone-600 underline" data-testid={`refund-manual-${nc.id}`}>Segna come rimborsato</button>}
      {open && (
        <div className="flex w-full flex-wrap items-center gap-2 rounded-xl bg-black/[0.03] p-2 text-xs" data-testid={`refund-manual-form-${nc.id}`}>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="h-8 rounded border border-black/15 px-2" data-testid={`refund-manual-method-${nc.id}`}>
            {["Bonifico", "Contanti in negozio", "Credito negozio", "Carta", "PayPal", "Altro"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 rounded border border-black/15 px-2" data-testid={`refund-manual-date-${nc.id}`} />
          <button onClick={() => act("refund/manual", { method, date })} className="rounded-full bg-cyan-700 px-3 py-1 font-bold text-white" data-testid={`refund-manual-save-${nc.id}`}>Conferma</button>
        </div>
      )}
    </>
  );
};

const RegisterExport = () => {
  const [ym, setYm] = useState(localDay().slice(0, 7));
  const go = () => { const [y, m] = ym.split("-"); download(`/admin/invoices/register?year=${y}&month=${parseInt(m, 10)}`, `registro-fatture-${ym}.xlsx`); };
  return (
    <div className="flex flex-wrap items-end gap-2 text-sm">
      <label className="space-y-1"><span className="text-xs text-stone-600">Registro del mese (Excel)</span>
        <input type="month" value={ym} onChange={(e) => setYm(e.target.value)} className={`${inp} w-44`} data-testid="register-month" /></label>
      <button onClick={go} disabled={!ym} className="flex h-11 items-center gap-2 rounded-full border-2 border-black px-4 font-semibold disabled:opacity-50" data-testid="register-download"><Download className="h-4 w-4" />Scarica per il commercialista</button>
    </div>
  );
};

const CreditNoteForm = ({ inv, onDone }) => {
  const credited = inv.credited || inv.lines?.map(() => 0) || [];
  const [qty, setQty] = useState(() => (inv.lines || []).map((l, k) => l.qty - (credited[k] || 0)));
  const [reason, setReason] = useState("Reso / rimborso");
  const [refund, setRefund] = useState(false);
  const submit = async (all) => {
    const lines = all ? null : qty.map((q, index) => ({ index, qty: parseInt(q, 10) || 0 })).filter((l) => l.qty > 0);
    if (!window.confirm(all ? `Stornare tutta la fattura n. ${inv.number}?` : "Creare la nota di credito per le quantità indicate?")) return;
    try {
      const { data } = await api.post(`/admin/invoices/${inv.id}/credit-note`, { lines, reason, refund });
      toast.success(`Nota di credito n. ${data.number} creata (${eur(data.total)})`);
      if (data.refund) toast[data.refund.status === "eseguito" ? "success" : "warning"](refundMsg(data.refund));
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="mt-2 w-full rounded-xl bg-black/[0.03] p-3 text-xs" data-testid={`credit-form-${inv.id}`}>
      {(inv.lines || []).map((l, k) => (
        <div key={k} className="flex items-center gap-2 py-1">
          <span className="flex-1">{l.desc}</span><span className="text-stone-500">stornabili {l.qty - (credited[k] || 0)}</span>
          <input type="number" min={0} max={l.qty - (credited[k] || 0)} value={qty[k]} onChange={(e) => setQty(qty.map((q, j) => (j === k ? e.target.value : q)))} className="h-8 w-16 rounded border border-black/15 px-2" data-testid={`credit-qty-${inv.id}-${k}`} />
        </div>
      ))}
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo" className="mt-2 h-9 w-full rounded border border-black/15 px-2" data-testid={`credit-reason-${inv.id}`} />
      <label className="mt-2 flex items-center gap-2 font-semibold"><input type="checkbox" checked={refund} onChange={(e) => setRefund(e.target.checked)} data-testid={`credit-refund-${inv.id}`} />Rimborsa anche il cliente (carta o PayPal, importo della nota)</label>
      <div className="mt-2 flex gap-2">
        <button onClick={() => submit(false)} className="rounded-full bg-cyan-700 px-3 py-1 font-bold text-white" data-testid={`credit-partial-${inv.id}`}>Crea nota parziale</button>
        <button onClick={() => submit(true)} className="rounded-full border border-rose-500 px-3 py-1 font-semibold text-rose-600" data-testid={`credit-total-${inv.id}`}>Storna tutto</button>
      </div>
    </div>
  );
};

const renumber = async (i, onDone) => {
  const v = window.prompt(`Nuovo numero per ${i.doc_type === "TD04" ? "la nota di credito" : "la fattura"} ${i.number} (solo il numero, anno ${i.year}):`, String(i.seq));
  if (!v || v === String(i.seq)) return;
  try { await api.put(`/admin/invoices/${i.id}/number`, { next_number: parseInt(v, 10) }); toast.success("Numero aggiornato"); onDone(); }
  catch (err) { toast.error(formatApiError(err)); }
};

const InvoiceBulkBar = ({ d, sel, setSel, onDone }) => {
  const del = async () => {
    const sent = d.invoices.filter((i) => sel.includes(i.id) && i.sdi);
    const msg = `Eliminare definitivamente ${sel.length} document${sel.length === 1 ? "o" : "i"}? Le note di credito collegate verranno eliminate insieme alle fatture.`
      + (sent.length ? `\n\nATTENZIONE: ${sent.length} sono già stati inviati allo SdI. Eliminarli qui non li annulla presso l'Agenzia delle Entrate.` : "")
      + "\n\nLa numerazione riparte dall'ultimo documento rimasto.";
    if (!window.confirm(msg)) return;
    try { const { data } = await api.post("/admin/invoices/delete", { ids: sel }); toast.success(`${data.done} documenti eliminati`); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-black/[0.03] px-3 py-2 text-xs" data-testid="invoice-bulk-bar">
      <label className="flex items-center gap-2"><input type="checkbox" checked={d.invoices.length > 0 && sel.length === d.invoices.length} onChange={(e) => setSel(e.target.checked ? d.invoices.map((i) => i.id) : [])} data-testid="invoice-select-all" />Seleziona tutte</label>
      {sel.length > 0 && <button onClick={del} className="rounded-full bg-rose-600 px-3 py-1 font-bold text-white" data-testid="invoice-delete-selected">Elimina {sel.length} selezionat{sel.length === 1 ? "a" : "e"}</button>}
      <span className="text-stone-500">Usa l'eliminazione per le fatture di prova.</span>
    </div>
  );
};

export const AdminInvoices = () => {
  const [d, setD] = useState(null);
  const [next, setNext] = useState("");
  const [sdi, setSdi] = useState(null);
  const [busy, setBusy] = useState(null);
  const [creditFor, setCreditFor] = useState(null);
  const [sel, setSel] = useState([]);
  const load = () => api.get("/admin/invoices").then((r) => { setD(r.data); setNext(String(r.data.next_number)); setSel([]); });
  useEffect(() => { load(); api.get("/admin/invoices/sdi-config").then((r) => setSdi(r.data)); }, []);
  const sdiAct = async (i, action) => {
    if (action === "send" && !window.confirm(`Inviare allo SdI ${i.doc_type === "TD04" ? "la nota di credito" : "la fattura"} n. ${i.number} a ${i.buyer.company || `${i.buyer.first_name} ${i.buyer.last_name}`}? Controlla prima il PDF: dopo l'invio non si può modificare.`)) return;
    setBusy(i.id);
    try {
      const { data } = await api.post(`/admin/invoices/${i.id}/sdi/${action}`);
      toast.success(action === "send" ? "Fattura inviata ad Aruba" : `Esito: ${data.sdi?.status}`); load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };
  const save = async () => {
    try { await api.put("/admin/invoices/settings", { next_number: parseInt(next, 10) }); toast.success("Numerazione aggiornata"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (!d) return null;
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="admin-invoices">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><FileText className="h-5 w-5 text-cyan-700" />Fatture e note di credito</p>
      <p className="mt-1 text-xs text-stone-500">Generate in automatico quando un ordine B2B, o un ordine privato con richiesta di fattura, risulta pagato; il cliente riceve un'email con il link. Controlla il PDF e poi premi "Invia allo SdI": la fattura parte tramite Aruba e qui vedi l'esito.</p>
      {sdi && (
        <p className={`mt-2 rounded-lg px-3 py-2 text-xs ${sdi.connected ? "bg-teal-500/10 text-teal-800" : "bg-amber-500/15 text-amber-800"}`} data-testid="sdi-config">
          {sdi.connected ? `Aruba collegato (${sdi.env === "prod" ? "produzione" : "ambiente di prova"})` : "Aruba non ancora collegato: servono utente e password del servizio Fatturazione Elettronica. Nel frattempo puoi scaricare l'XML e caricarlo a mano."}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-6">
        <div className="flex items-end gap-2 text-sm">
          <label className="space-y-1"><span className="text-xs text-stone-600">Prossimo numero {d.year}</span>
            <input type="number" min={1} value={next} onChange={(e) => setNext(e.target.value)} className={`${inp} w-32`} data-testid="invoice-next-number" /></label>
          <button onClick={save} className="h-11 rounded-full bg-amber-500 px-4 font-bold text-black" data-testid="invoice-next-save">Salva</button>
        </div>
        <RegisterExport />
      </div>
      <InvoiceBulkBar d={d} sel={sel} setSel={setSel} onDone={load} />
      <ul className="mt-2 divide-y divide-black/5 text-sm">
        {d.invoices.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center gap-3 py-2" data-testid={`invoice-${i.id}`}>
            <input type="checkbox" checked={sel.includes(i.id)} onChange={(e) => setSel(e.target.checked ? [...sel, i.id] : sel.filter((x) => x !== i.id))} data-testid={`invoice-select-${i.id}`} />
            <span className="font-mono font-semibold">{i.doc_type === "TD04" ? "NC" : "n."} {i.number}</span><span className="text-stone-500">{i.date}</span>
            {!i.sdi && <button onClick={() => renumber(i, load)} className="text-[11px] text-stone-500 underline" data-testid={`invoice-renumber-${i.id}`}>cambia numero</button>}
            <span>{i.buyer.company || `${i.buyer.first_name} ${i.buyer.last_name}`}{i.b2c && <span className="ml-1 text-[10px] text-stone-500">privato</span>}</span><span className="font-mono">{i.doc_type === "TD04" ? "-" : ""}{eur(i.total)}</span>
            {i.related && <span className="text-xs text-stone-500">storno fatt. {i.related.number}</span>}
            {i.doc_type === "TD04" && <RefundBadge nc={i} onDone={load} />}
            <button onClick={() => download(`/invoices/${i.id}/pdf`, `fattura-${i.number.replace("/", "-")}.pdf`)} className="ml-auto text-xs text-cyan-700 underline" data-testid={`invoice-pdf-${i.id}`}>PDF</button>
            <button onClick={() => download(`/admin/invoices/${i.id}/xml`, `IT01900060888_${i.progressive}.xml`)} className="text-xs text-cyan-700 underline" data-testid={`invoice-xml-${i.id}`}>XML SdI</button>
            {i.sdi?.status && <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${["Consegnata", "Accettata", "Decorrenza termini"].includes(i.sdi.status) ? "bg-teal-500/15 text-teal-700" : ["Scartata", "Errore elaborazione", "Rifiutata"].includes(i.sdi.status) ? "bg-rose-500/15 text-rose-700" : "bg-amber-500/15 text-amber-700"}`} title={i.sdi.description || ""} data-testid={`invoice-sdi-status-${i.id}`}>SdI: {i.sdi.status}</span>}
            {(!i.sdi?.filename || ["Scartata", "Errore elaborazione"].includes(i.sdi?.status)) && sdi?.connected && <button disabled={busy === i.id} onClick={() => sdiAct(i, "send")} className="rounded-full bg-cyan-700 px-3 py-1 text-xs font-bold text-white disabled:opacity-50" data-testid={`invoice-sdi-send-${i.id}`}>{i.sdi?.filename ? "Reinvia allo SdI" : "Invia allo SdI"}</button>}
            {i.sdi?.filename && <button disabled={busy === i.id} onClick={() => sdiAct(i, "refresh")} className="text-xs text-stone-600 underline" data-testid={`invoice-sdi-refresh-${i.id}`}>Aggiorna esito</button>}
            {i.email_sent_at === undefined ? null : !i.email_sent_at && <span className="text-xs text-rose-600" data-testid={`invoice-email-failed-${i.id}`}>email non inviata</span>}
            {i.doc_type !== "TD04" && (i.lines || []).some((l, k) => l.qty > ((i.credited || [])[k] || 0)) && <button onClick={() => setCreditFor(creditFor === i.id ? null : i.id)} className="text-xs text-rose-600 underline" data-testid={`invoice-credit-${i.id}`}>Nota di credito</button>}
            {creditFor === i.id && <CreditNoteForm inv={i} onDone={() => { setCreditFor(null); load(); }} />}
          </li>
        ))}
        {!d.invoices.length && <li className="py-2 text-stone-500">Nessuna fattura emessa.</li>}
      </ul>
    </div>
  );
};
